// OwnedItemDetailDialog — the owned-item "Details" view (TD-671 Task 13,
// operator rulings R9 and R13).
//
// R9 moved the cancel control off the row (it was the R4 "Cancel renewal" link)
// into this dialog as a small "Cancel subscription" link, shown ONLY when the
// server sent a `cancel` action for the item (`placement: "detail"`). The link
// does not cancel anything itself: it hands the subscription id to the owner
// (ProductsTab), which closes this dialog and opens the existing cancel
// confirmation — the POST, Idempotency-Key and §59 re-seed live there,
// unchanged.
//
// R13: a `help` action is API-only (the www side opens its AI chat with the
// action's `context`). It is deliberately NOT rendered here or on the row:
// no Help button, no drawer slot. Only `cancel` is read from `actions`.
//
// The body is a plain summary of what the item is (status, amount, store or
// quantity, and the purchases it came from), built from fields the server
// already sent — nothing here is fetched. Inline styles only — no Tailwind.

import { useEffect, useRef, type ReactNode } from "react";
import { useEmbedTokens, EmbedButton, EmbedDialog, EmbedText } from "../../embed-shared/primitives";
import { formatCalendarDate, formatInstantDate } from "../../embed-shared/format";
import type { Subscription } from "../../embed-shared/types";
import type { OwnedItemWire } from "./owned-products-api";
import { parseDollarAmount } from "./product-display";
import { PricingDisplay } from "./PricingDisplay";
import { SubscriptionRowLink } from "./SubscriptionRowLink";

export interface OwnedItemDetailDialogProps {
  /** The item to describe; null keeps the dialog closed. */
  item: OwnedItemWire | null;
  onClose: () => void;
  /** The element focus returns to on close (the row's Details button). */
  returnFocusTo?: HTMLElement | null;
  /** The subscription's actions have not been re-read since its last mutation:
   *  offer no cancel link even if a stale action is present. */
  pending?: boolean;
  /** Called with the `cancel` action's subscription id. */
  onCancelSubscription: (subscriptionId: number) => void;
}

function subscriptionStatusText(sub: Subscription): string {
  if (sub.status === "canceled") return "Ended";
  if (sub.cancel_at) return `Cancels ${formatCalendarDate(sub.service_end_date ?? sub.cancel_at)}`;
  return "Active";
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  const t = useEmbedTokens();
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: t.spacing.md, alignItems: "baseline" }}>
      <EmbedText tone="secondary" size="xs">
        {label}
      </EmbedText>
      <div style={{ textAlign: "right", fontSize: t.fontSize.sm, color: t.colors.text }}>{children}</div>
    </div>
  );
}

export function OwnedItemDetailDialog({
  item,
  onClose,
  returnFocusTo,
  pending = false,
  onCancelSubscription,
}:OwnedItemDetailDialogProps) {
  const t = useEmbedTokens();
  // Coordinator ruling: initial focus is the Close button, never the cancel
  // link. showModal() focuses the first focusable control in DOM order, which is
  // the link (it sits in the body, before the footer), so move focus once the
  // dialog is open. EmbedDialog is untouched: other dialogs keep their behaviour.
  const footerRef = useRef<HTMLDivElement | null>(null);
  const isOpen = item != null;
  useEffect(() => {
    if (isOpen) footerRef.current?.querySelector("button")?.focus();
  }, [isOpen]);
  const cancel = pending ? undefined : item?.actions.find((a) => a.type === "cancel");
  const sub = item?.subscription ?? null;
  const amount = sub ? parseDollarAmount(sub.recurring_amount) : null;

  return (
    <EmbedDialog
      open={item != null}
      onClose={onClose}
      title={item ? `${item.product.name} details` : "Details"}
      size="small"
      returnFocusTo={returnFocusTo}
      footer={
        <div ref={footerRef} style={{ display: "contents" }}>
          <EmbedButton type="button" variant="secondary" onClick={onClose}>
            Close
          </EmbedButton>
        </div>
      }
    >
      {item && (
        <div data-testid="owned-item-detail" style={{ display: "flex", flexDirection: "column", gap: t.spacing.sm }}>
          {sub && (
            <>
              <Fact label="Status">{subscriptionStatusText(sub)}</Fact>
              {amount != null && (
                <Fact label="Amount">
                  <PricingDisplay amount={amount} recurringLabel={`/${sub.recurring_interval}`} />
                </Fact>
              )}
              {item.store && <Fact label="Store">{item.store.name}</Fact>}
            </>
          )}
          {item.kind === "one_time" && <Fact label="Owned">{item.quantity_owned ?? 0}</Fact>}
          {/* Purchase times are TIMESTAMPTZ instants -> the viewer's local day
              (formatInstantDate); only the subscription's DATE columns above use
              formatCalendarDate. */}
          {item.first_purchased_at && <Fact label="First purchased">{formatInstantDate(item.first_purchased_at)}</Fact>}
          {item.last_purchased_at && <Fact label="Last purchased">{formatInstantDate(item.last_purchased_at)}</Fact>}
          {item.lines.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 2, marginTop: t.spacing.xs }}>
              <EmbedText tone="secondary" size="xs">
                Purchases
              </EmbedText>
              <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 2 }}>
                {item.lines.map((l) => (
                  <li key={l.order_line_id} style={{ fontSize: t.fontSize.sm, color: t.colors.text }}>
                    {`${l.order_number} · qty ${l.quantity} · ${formatInstantDate(l.placed_at)}`}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {cancel && cancel.type === "cancel" && (
            <div style={{ marginTop: t.spacing.sm }}>
              <SubscriptionRowLink
                label="Cancel subscription"
                onClick={() => onCancelSubscription(cancel.subscription_id)}
              />
            </div>
          )}
        </div>
      )}
    </EmbedDialog>
  );
}
